import { BOTTOM_BAR_FLOATING_OFFSET } from "@ui/components/BottomBar";
import type { MapPaddingObject } from "@ui/components/Map";

// Shared geometry for the mobile Airbnb-style map + bottom-sheet layout. Both the layout
// composition (mobile-search-map.tsx) and the search adapters read these so the sheet's peek
// detent, the floating pin card's offset, and the map's fit/selection padding all derive from
// one source of truth (avoids the sheet and the pin card drifting apart across viewports).

/** Footprint of the floating Dock (`AppBottomBar`), which renders below `md` only. It overlays
 *  the page, so scroll containers running under it pad by this much. */
export const MOBILE_DOCK_OFFSET = BOTTOM_BAR_FLOATING_OFFSET;

/** Numeric stand-in for {@link MOBILE_DOCK_OFFSET} where a CSS calc is unusable — Mapbox padding
 *  takes numbers. Assumes no home-indicator inset (devices that have one carry a Dock ~18px
 *  taller) and a 16px root font, since it resolves `3.5rem + 1rem` at that size; a user root font
 *  other than 16px desyncs it from the rem-based `BOTTOM_BAR_FLOATING_OFFSET`. Both errors only
 *  over- or under-frame the pin. */
export const MOBILE_DOCK_H = 72;

/** Numeric stand-in for `FRAME_TOPBAR_HEIGHT_VAR`'s 5.5rem pre-measure fallback, used to size the
 *  map canvas (which starts below the top bar). Over-estimating the bar is the safe direction: it
 *  only tightens the selection-padding clamp below. */
const MOBILE_TOPBAR_H = 88;

/** Share of the map canvas the selection padding may consume; the rest stays free framing space. */
const MOBILE_SELECTION_PADDING_RATIO = 0.75;

/** Visible sheet height at the `peek` detent — grabber band + the "N results" header. */
export const MOBILE_PEEK_PX = 88;

/** Approximate height of the floating pin card, used to lift a selected pin clear of it. */
export const MOBILE_PINCARD_H = 360;

/** Gap between the floating pin card and the top of the peeked sheet. */
export const MOBILE_PINCARD_GAP = 12;

/**
 * fitBounds padding (map canvas starts below the top bar and runs to the viewport bottom) that
 * frames all pins into the map band left visible above the half-open sheet. Bottom padding ≈ the
 * occluded lower half of the viewport.
 */
export function mobileFitPadding(viewportHeight: number): MapPaddingObject {
  return { top: 32, left: 24, right: 24, bottom: Math.round(viewportHeight * 0.5) };
}

export interface MobileSelectionPaddingOptions {
  /** Whether the floating Dock renders (below `md` only); the mobile map layout also runs in the
   *  md–lg band, where there is no Dock to clear. */
  hasDock: boolean;
  viewportHeight: number;
}

/**
 * easeTo padding used when a pin is selected: keeps the pin above the peeked sheet AND the
 * floating pin card that renders just above it. The peeked sheet is measured from the viewport
 * bottom, so it clears the Dock the sheet runs beneath where the Dock exists.
 *
 * Bottom padding is clamped to the map canvas: mapbox-gl silently pins the centre to the canvas
 * edge when padding exceeds it, so on short (landscape) viewports the unclamped ideal would jam
 * the selected pin flush against the top edge instead of framing it.
 */
export function mobileSelectionPadding({ hasDock, viewportHeight }: MobileSelectionPaddingOptions): MapPaddingObject {
  const top = 24;
  const ideal = (hasDock ? MOBILE_DOCK_H : 0) + MOBILE_PEEK_PX + MOBILE_PINCARD_H + MOBILE_PINCARD_GAP;
  const canvasHeight = Math.max(viewportHeight - MOBILE_TOPBAR_H, 0);
  const maxBottom = Math.max(canvasHeight * MOBILE_SELECTION_PADDING_RATIO - top, 0);

  return { top, left: 24, right: 24, bottom: Math.round(Math.min(ideal, maxBottom)) };
}
